import { useState, useEffect } from 'react'
import { Loader2, Save } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import Modal from '@/components/Modal'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import SearchableSelect from '@/components/SearcheableSelect'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  SheetConnector,
  SheetConnectorEdge,
  SheetConnectorDirection,
  CreateSheetConnectorRequest,
  UpdateSheetConnectorRequest,
} from '@/types/sheet-connector'

interface SheetConnectorModalProps {
  isOpen: boolean
  onClose: () => void
  documentId: string
  defaultEdge: SheetConnectorEdge
  editingConnector: SheetConnector | null
  multiPageDocs: { id: string; name: string }[]
  existingConnectorIds: string[]
  onCreate: (dto: CreateSheetConnectorRequest) => Promise<unknown>
  onUpdate: (id: string, dto: UpdateSheetConnectorRequest) => Promise<unknown>
}

const EDGES: { label: string; value: SheetConnectorEdge }[] = [
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
]

const DIRECTIONS: { label: string; value: SheetConnectorDirection }[] = [
  { label: 'Outbound', value: 'out' },
  { label: 'Inbound', value: 'in' },
]

export default function SheetConnectorModal({
  isOpen,
  onClose,
  documentId,
  defaultEdge,
  editingConnector,
  multiPageDocs,
  existingConnectorIds,
  onCreate,
  onUpdate,
}: SheetConnectorModalProps) {
  const { t } = useTranslation()
  const isEdit = !!editingConnector
  const [saving, setSaving] = useState(false)

  // Form state
  const [connectorId, setConnectorId] = useState('')
  const [edge, setEdge] = useState<SheetConnectorEdge>(defaultEdge)
  const [direction, setDirection] = useState<SheetConnectorDirection>('out')
  const [targetDocumentId, setTargetDocumentId] = useState('')
  const [label, setLabel] = useState('')
  const [position, setPosition] = useState('')

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return
    if (editingConnector) {
      setConnectorId(editingConnector.connector_id)
      setEdge(editingConnector.edge)
      setDirection(editingConnector.direction)
      setTargetDocumentId(editingConnector.target_document_id ?? '')
      setLabel(editingConnector.label ?? '')
      setPosition(editingConnector.position != null ? String(editingConnector.position) : '')
    } else {
      setConnectorId('')
      setEdge(defaultEdge)
      setDirection('out')
      setTargetDocumentId('')
      setLabel('')
      setPosition('')
    }
    setErrors({})
  }, [isOpen, editingConnector, defaultEdge])

  const validate = (): boolean => {
    const e: Record<string, string> = {}
    if (!connectorId.trim()) {
      e.connectorId = t('data_catalog:graph_editor.validation.connector_id_required')
    } else if (!isEdit && existingConnectorIds.includes(connectorId.trim())) {
      e.connectorId = t('data_catalog:graph_editor.validation.connector_id_exists')
    } else if (isEdit && connectorId.trim() !== editingConnector!.connector_id && existingConnectorIds.includes(connectorId.trim())) {
      e.connectorId = t('data_catalog:graph_editor.validation.connector_id_exists')
    }
    if (targetDocumentId === documentId) {
      e.targetDocumentId = t('data_catalog:graph_editor.validation.cannot_link_same_sheet')
    }
    if (position !== '') {
      const num = parseFloat(position)
      if (isNaN(num) || num < 0 || num > 1) {
        e.position = t('data_catalog:graph_editor.validation.position_range')
      }
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      if (isEdit) {
        const dto: UpdateSheetConnectorRequest = {
          connector_id: connectorId.trim(),
          edge,
          direction,
          target_document_id: targetDocumentId || null,
          label: label.trim() || null,
          position: position !== '' ? parseFloat(position) : null,
        }
        await onUpdate(editingConnector!.id, dto)
        toast.success(t('data_catalog:graph_editor.toasts.connector_updated'))
      } else {
        const dto: CreateSheetConnectorRequest = {
          document_id: documentId,
          connector_id: connectorId.trim(),
          edge,
          direction,
          target_document_id: targetDocumentId || undefined,
          label: label.trim() || undefined,
          position: position !== '' ? parseFloat(position) : undefined,
        }
        await onCreate(dto)
        toast.success(t('data_catalog:graph_editor.toasts.connector_created'))
      }
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('data_catalog:graph_editor.toasts.connector_save_failed'))
    } finally {
      setSaving(false)
    }
  }

  const targetOptions = multiPageDocs
    .filter((d) => d.id !== documentId)
    .map((d) => ({ label: d.name, value: d.id }))

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('data_catalog:graph_editor.sheet_connector.edit_title') : t('data_catalog:graph_editor.sheet_connector.add_title')}
      className="w-full max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('data_catalog:graph_editor.sheet_connector.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !connectorId.trim()}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEdit ? t('data_catalog:graph_editor.sheet_connector.update') : t('data_catalog:graph_editor.sheet_connector.create')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 py-4">
        <div className="grid gap-2">
          <Label htmlFor="sc-connector-id">{t('data_catalog:graph_editor.sheet_connector.connector_id_label')}</Label>
          <Input
            id="sc-connector-id"
            value={connectorId}
            onChange={(e) => setConnectorId(e.target.value)}
            placeholder={t('data_catalog:graph_editor.placeholders.connector_id')}
            maxLength={64}
          />
          {errors.connectorId && (
            <p className="text-sm text-red-500">{errors.connectorId}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label>{t('data_catalog:graph_editor.sheet_connector.edge_label')}</Label>
            <Select value={edge} onValueChange={(v) => setEdge(v as SheetConnectorEdge)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EDGES.map((e) => (
                  <SelectItem key={e.value} value={e.value}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>{t('data_catalog:graph_editor.sheet_connector.direction_label')}</Label>
            <Select value={direction} onValueChange={(v) => setDirection(v as SheetConnectorDirection)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIRECTIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-2">
          <Label>{t('data_catalog:graph_editor.sheet_connector.target_sheet_label')}</Label>
          <SearchableSelect
            options={[{ label: t('data_catalog:graph_editor.sheet_connector.none_option'), value: '' }, ...targetOptions]}
            value={targetDocumentId}
            onChange={setTargetDocumentId}
            placeholder={t('data_catalog:graph_editor.placeholders.target_sheet')}
          />
          {errors.targetDocumentId && (
            <p className="text-sm text-red-500">{errors.targetDocumentId}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="sc-label">{t('data_catalog:graph_editor.sheet_connector.label_label')}</Label>
          <Input
            id="sc-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('data_catalog:graph_editor.placeholders.display_label')}
            maxLength={255}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="sc-position">{t('data_catalog:graph_editor.sheet_connector.position_label')}</Label>
          <Input
            id="sc-position"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder={t('data_catalog:graph_editor.placeholders.position')}
            type="number"
            min={0}
            max={1}
            step={0.1}
          />
          {errors.position && (
            <p className="text-sm text-red-500">{errors.position}</p>
          )}
        </div>
      </div>
    </Modal>
  )
}
